// Dependencies
import { EnumRadioField, fieldTypes, utils } from "@ucl/library";

import {
  BoltTaskStatuses,
  BoltTaskTypePriorities,
  IBoltTaskTemplate,
  TaskExecutionTypeFieldSchema,
} from "@bolt/core";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import { FC } from "react";
import { useTaskTemplateForm } from "../useTaskTemplateForm";
import { multiStepTaskTypeForm } from "./multiStepsTaskTemplateForm";
import { singleStepTaskTypeForm } from "./singleStepTaskTemplateForm";
import { RichTextEditorsOutputTextTypes } from "../../../types/globalTypes";
import { useBoltTaskTypeActionOptions } from "../useBoltTaskTemplateActionOptions";
import { isNil } from "lodash";

export const optionalTaskStatus =
  utils.enums.convertEnumToOptions(BoltTaskStatuses);
export const taskPriorityOptions = utils.enums.convertEnumToOptions(
  BoltTaskTypePriorities
);

export interface BoltTaskTemplateFormProps {
  taskTemplates: IBoltTaskTemplate[];
  updateTaskTemplates: (taskTemplate: IBoltTaskTemplate[]) => void;
  apiBaseUrl: string;
  assigneeOdataUrl: string;
  getAuthRequestHeader: () => Promise<Record<string, string> | undefined>;
  richTextEditorsOutputTextType?: RichTextEditorsOutputTextTypes;
  errors?: { [key: string]: string[] };
  taskExecutionTypeData: TaskExecutionTypeFieldSchema;
  updateTaskExecutionTypeData: (
    updatedModel: TaskExecutionTypeFieldSchema
  ) => void;
  workflowType?: "Single Step" | "Multi-Step";
  showWorkflowTypeSelector?: boolean;
  entityTypeOptions?: fieldTypes.Option<string>[] | [];
}

export const CreateBoltTaskTemplateFormContainer: FC<
  BoltTaskTemplateFormProps
> = ({
  taskTemplates,
  updateTaskTemplates,
  apiBaseUrl,
  assigneeOdataUrl,
  getAuthRequestHeader,
  richTextEditorsOutputTextType,
  errors,
  taskExecutionTypeData,
  updateTaskExecutionTypeData,
  workflowType,
  showWorkflowTypeSelector = true,
  entityTypeOptions,
}) => {
  const { taskExecutionTypeOptions } = useBoltTaskTypeActionOptions();

  const { parentTaskTemplate, schemaFactory, updateParentTaskTemplate } =
    useTaskTemplateForm({
      taskTemplates,
      updateTaskTemplates,
      workflowType,
    });

  if (!schemaFactory) {
    return;
  }

  return (
    <div className="bolt-task-type-form-content-container">
      <div>
        <TooltipComponent
          content={
            '"Single Step" = This task template will only have a single workflow step associated with it.'
          }
          target=".workflow-steps-selector label.bp5-radio:first-of-type"
          position="BottomCenter"
        >
          {showWorkflowTypeSelector && (
            <TooltipComponent
              content={
                '"Multi-Step" - This task template will have multiple workflow steps associated with it.'
              }
              target=".workflow-steps-selector label.bp5-radio:last-of-type"
              position="BottomCenter"
            >
              <div className="workflow-steps-selector">
                <EnumRadioField
                  label="Workflow"
                  type="EnumRadio"
                  fieldName="hasMultiSteps"
                  fieldKey="hasMultiSteps"
                  disabled={!isNil(workflowType)}
                  optionValues={[
                    { label: "Single Step", value: 1 },
                    { label: "Multi-step", value: 2 },
                  ]}
                  value={parentTaskTemplate?.hasMultiSteps ? 2 : 1}
                  onSubmit={(value) => {
                    updateTaskTemplates(
                      taskTemplates.map((ogTT) =>
                        ogTT.orderNumber === 0
                          ? { ...ogTT, hasMultiSteps: value === 2 }
                          : ogTT
                      )
                    );
                  }}
                  isRequired
                />
              </div>
            </TooltipComponent>
          )}
        </TooltipComponent>
        {parentTaskTemplate.hasMultiSteps
          ? multiStepTaskTypeForm(
              schemaFactory,
              apiBaseUrl,
              assigneeOdataUrl,
              taskTemplates,
              updateTaskTemplates,
              getAuthRequestHeader,
              richTextEditorsOutputTextType,
              errors
            )
          : singleStepTaskTypeForm(
              schemaFactory,
              apiBaseUrl,
              assigneeOdataUrl,
              parentTaskTemplate,
              updateParentTaskTemplate,
              getAuthRequestHeader,
              richTextEditorsOutputTextType,
              errors,
              taskExecutionTypeOptions,
              taskExecutionTypeData,
              updateTaskExecutionTypeData,
              entityTypeOptions
            )}
      </div>
    </div>
  );
};
